import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { lazy, Suspense } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';

import { AdminAccountsPage } from '@/routes/AdminAccountsPage';
import { AuditLogPage } from '@/routes/AuditLogPage';
import { AuthenticatedShell } from '@/routes/AuthenticatedShell';
import { GrantsPage } from '@/routes/GrantsPage';
import { LoginPage } from '@/routes/LoginPage';
import { OrganizationLookupPage } from '@/routes/OrganizationLookupPage';
import { CreditsPage } from '@/routes/organizations/CreditsPage';
import { PlanPage } from '@/routes/organizations/PlanPage';
import { RefundCalculatorPage } from '@/routes/organizations/RefundCalculatorPage';
import { OverviewPage } from '@/routes/OverviewPage';
import { PaymentsLookupPage } from '@/routes/PaymentsLookupPage';
import { ReportsPage } from '@/routes/ReportsPage';
import { SkillsPage } from '@/routes/SkillsPage';
import { SystemPromptsPage } from '@/routes/SystemPromptsPage';
import { SpendPage } from '@/routes/SpendPage';
import { RequireSession } from '@/routes/RequireSession';
import { UserDetailPage } from '@/routes/UserDetailPage';
import { UserPaymentsPage } from '@/routes/UserPaymentsPage';
import { UsersPage } from '@/routes/UsersPage';
import { WorkflowsPage } from '@/routes/WorkflowsPage';

// The canvas pulls in @xyflow/react; load it only when a workflow is opened.
const WorkflowDetailPage = lazy(() => import('@/routes/workflows/WorkflowDetailPage'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
    },
  },
});

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route element={<RequireSession />}>
            <Route element={<AuthenticatedShell />}>
              <Route path="/" element={<OverviewPage />} />
              <Route path="/audit" element={<AuditLogPage />} />
              {/* The grants panel (869f084w5 §4) — the SAME append-only audit
                  rows as /audit, projected so who / when / platform / how much
                  or which plan or benefit / validity are COLUMNS rather than
                  keys inside a collapsed payload blob. */}
              <Route path="/grants" element={<GrantsPage />} />
              {/* Console account management (869f0ev3c access model): any role
                  reads it; only a superadmin gets the controls. */}
              <Route path="/admin-accounts" element={<AdminAccountsPage />} />
              {/* Reports about users (869f4c9vb) — superadmin only; the nav
                  hides it for every other role and the API refuses them. */}
              <Route path="/reports" element={<ReportsPage />} />
              {/* The agent's system prompts (moved here from the product's
                  Settings -> Agent) — superadmin only; the nav hides it and the
                  API refuses every other role. */}
              <Route path="/system-prompts" element={<SystemPromptsPage />} />
              {/* The global agent skills — superadmin only; the nav hides it and
                  the API refuses every other role. */}
              <Route path="/skills" element={<SkillsPage />} />
              {/* What generations cost us (1amz0xe2evr) — every console role,
                  viewer included; users appear as aggregates only. */}
              <Route path="/spend" element={<SpendPage />} />
              {/* User workflows: anyone's workflow, read-only and audited
                  (superadmin / `workflows_read`). */}
              <Route path="/workflows" element={<WorkflowsPage />} />
              <Route
                path="/workflows/:id"
                element={
                  <Suspense fallback={<p className="text-body text-fg-hint">Loading…</p>}>
                    <WorkflowDetailPage />
                  </Suspense>
                }
              />
              {/* Content history is reached PER USER (§1.4) — there is no
                  standalone content list, so the "Content" nav item also
                  points here; a user's Content tab is the entry point. The
                  reset-password action (869f0ev1x) is folded into the
                  Identity tab of this same card — see ResetPasswordDialog's
                  `knownEmail` seam, built for exactly this — so there is no
                  separate /users/:id/actions route. */}
              <Route path="/users" element={<UsersPage />} />
              <Route path="/users/:userId" element={<UserDetailPage />} />
              {/* Support-admin Phase 2 (869f0ev2j/869f0ev2r): credit grants
                  and manual plan/benefit activation, reached via its own
                  org lookup rather than folded into UserDetailPage's
                  Organization tab (that fold is a follow-up, not this
                  merge's call to make). */}
              <Route path="/organizations" element={<OrganizationLookupPage />} />
              <Route path="/organizations/:orgId/credits" element={<CreditsPage />} />
              <Route path="/organizations/:orgId/plan" element={<PlanPage />} />
              {/* Coin-refund calculator (869f7jkj5): refund a share of a
                  purchase and take back the same share of its coins. */}
              <Route
                path="/organizations/:orgId/refund-calculator"
                element={<RefundCalculatorPage />}
              />
              {/* Support-admin payment bridge + admin-initiated deletion
                  (869f0ev24/869f0ev30): its own lookup, distinct from the
                  Users card above — payment-token deletion needs the
                  payments-service view, not the product profile. */}
              <Route path="/payments" element={<PaymentsLookupPage />} />
              <Route path="/users/:id/payments" element={<UserPaymentsPage />} />
            </Route>
          </Route>
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
